'use client';

import i18n from 'i18next';
import Backend from 'i18next-chained-backend';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, DEFAULT_LOCALE_NAME, getLanguageByCode, type SupportedLanguage } from '@/lib/i18n/config';
import { fetchSupportedLanguages, setLanguageCookie, createBackendConfig } from '@/lib/i18n/utils';
import { webStorageUtil } from '@/lib/storage/webStorageUtil';
import { ELocalStorageKeys } from '@/lib/storage/enums';
import { resolveLanguage, readIsLanguageExplicitlySet, setLanguageExplicitlySelected } from '@web-store-platforms/locales';
import baseMessages from '@web-store-platforms/locales/en/common.json';
import overrides from '../../../locales/en.overrides.json';
import { deepMerge } from '@web-store-platforms/locales';

const enMessages = deepMerge(baseMessages, overrides);
import { LocalizationState } from '@/hooks/useLocalizationState';
import coralogixRumService from './coralogixRum';
import { LoggerEventNames } from '../consts/logger';
import { getEnvVariable } from "@/lib/utils/env";;

interface LanguageServiceConfig {
    publisherId?: string;
    initialLanguage?: string;
    baseURL?: string;
}

interface LanguageServiceState {
    isReady: boolean;
    currentLanguage: string;
    supportedLanguages: string[];
}

class LanguageService {
    private state: LanguageServiceState = {
        isReady: false,
        currentLanguage: DEFAULT_LOCALE,
        supportedLanguages: [DEFAULT_LOCALE],
    };

    private languagesList: SupportedLanguage[] = [];
    private listeners: Set<(state: LanguageServiceState) => void> = new Set();

    static initializeI18n() {
        i18n.use(initReactI18next).init({
            fallbackLng: DEFAULT_LOCALE,
            lng: DEFAULT_LOCALE,
            resources: {
                [DEFAULT_LOCALE]: {
                    translation: enMessages,
                },
            },
            interpolation: {
                escapeValue: false,
            },
            ns: ['translation'],
            defaultNS: 'translation',
            preload: [DEFAULT_LOCALE],
            supportedLngs: [DEFAULT_LOCALE],
            debug: getEnvVariable('NODE_ENV') !== 'production',
        });
    }

    translate(key: string): string {
        return i18n.t(key);
    }
    async initialize(config: LanguageServiceConfig = {}, localizationState: LocalizationState): Promise<void> {
        const { publisherId, initialLanguage, baseURL } = config;

        try {
            this.setState({ isReady: false });

            const savedLanguage = webStorageUtil.get(ELocalStorageKeys.CURRENT_LANGUAGE);
            const isExplicitlySelected = readIsLanguageExplicitlySet();
            let supportedLanguages = [DEFAULT_LOCALE];

            let languagesList: SupportedLanguage[] = [];

            if (publisherId && baseURL) {
                try {
                    const result = await fetchSupportedLanguages(publisherId, localizationState);
                    supportedLanguages = result.supportedLanguages;
                    languagesList = result.languagesList;
                    this.languagesList = languagesList;
                } catch (error) {
                    console.warn('Failed to fetch supported languages, using default:', error);
                }
            }

            const bootLocaleIso = initialLanguage
                ? (languagesList.find((l) => l.language === initialLanguage || l.isoCode === initialLanguage)?.isoCode ?? initialLanguage)
                : null;

            const languageToUse = resolveLanguage({
                savedLanguage,
                isExplicitlySelected,
                geoCountryCode: null,
                bootLocale: bootLocaleIso,
                supportedLanguages: languagesList,
            });
            localizationState.setCurrentLanguage(languageToUse);
            this.setState({
                currentLanguage: languageToUse,
                supportedLanguages,
            });

            if (savedLanguage !== languageToUse) {
                webStorageUtil.set(ELocalStorageKeys.CURRENT_LANGUAGE, languageToUse);
            }

            setLanguageCookie(languageToUse, this.resolveDisplayName(languageToUse));

            const backendConfig = publisherId && baseURL
                ? createBackendConfig(publisherId, baseURL, languagesList)
                : undefined;

            await i18n.use(Backend).init({
                ...i18n.options,
                ...(backendConfig && { backend: backendConfig }),
                supportedLngs: supportedLanguages,
                lng: languageToUse,
                fallbackLng: DEFAULT_LOCALE,
            });

            i18n.addResourceBundle(DEFAULT_LOCALE, 'translation', enMessages, true, true);

            localizationState.setIsI18nReady(true);

            this.setState({ isReady: true });

            if (languageToUse !== DEFAULT_LOCALE) {
                await this.changeLanguage(languageToUse, localizationState);
            }
        } catch (error) {
            coralogixRumService.sendErrorEvent(LoggerEventNames.LANGUAGE_ERROR, {
                error: error
            });
            this.setState({ isReady: true });
        }
    }

    async changeLanguage(language: string, localizationState: LocalizationState, isUserInitiated = false): Promise<void> {
        try {
            if (!this.state.supportedLanguages.includes(language)) {
                console.warn(`Language ${language} is not supported`);
                return;
            }

            if (language !== DEFAULT_LOCALE) {
                await i18n.reloadResources(language);
            }
            i18n.addResourceBundle(DEFAULT_LOCALE, 'translation', enMessages, true, true);
            await i18n.changeLanguage(language);

            this.setState({ currentLanguage: language });
            webStorageUtil.set(ELocalStorageKeys.CURRENT_LANGUAGE, language);
            setLanguageCookie(language, this.resolveDisplayName(language));
            localizationState.setCurrentLanguage(language);

            if (isUserInitiated) {
                setLanguageExplicitlySelected();
            }
        } catch (error) {
            coralogixRumService.sendErrorEvent(LoggerEventNames.LANGUAGE_ERROR, {
                error: error
            });
            if (language !== DEFAULT_LOCALE) {
                await i18n.changeLanguage(DEFAULT_LOCALE);
                this.setState({ currentLanguage: DEFAULT_LOCALE });
            }
        }
    }

    getCurrentLanguage(): string {
        return this.state.currentLanguage;
    }

    getCurrentLanguageDisplayName(): string {
        return this.resolveDisplayName(this.state.currentLanguage);
    }

    private resolveDisplayName(isoCode: string): string {
        const fromList = this.languagesList.find((lang) => lang.isoCode === isoCode);
        if (fromList) return fromList.language;
        return getLanguageByCode(isoCode)?.language ?? DEFAULT_LOCALE_NAME;
    }

    getSupportedLanguages(): string[] {
        return this.state.supportedLanguages;
    }

    isReady(): boolean {
        return this.state.isReady;
    }

    subscribe(listener: (state: LanguageServiceState) => void): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    private setState(updates: Partial<LanguageServiceState>): void {
        this.state = { ...this.state, ...updates };
        this.listeners.forEach((listener) => listener(this.state));
    }

    getI18n() {
        return i18n;
    }
}

LanguageService.initializeI18n();

const languageService = new LanguageService();

export { languageService, i18n };
export type { LanguageServiceConfig, LanguageServiceState };
